"use client"

import { Text } from "@/elements"
import React, { FC } from "react"
import { FragmentType, graphql, useFragment } from "@/gql"
import { formatDistanceToNow } from "date-fns"
import { ChevronUpIcon } from "@/icons/outline/ChevronUpIcon"
import { JobInvitationStatus } from "@/gql/graphql"
import clsx from "clsx"
import { JobStatusTagStatus, JobStatusTag } from "@/components/JobStatusTag"
import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline"
import { UserAvatarWithNameAndTitle } from "@/components"
import adjustDateForTimezone from "@/utils/dates"

const JobDetailsFooterInvitationBannerFragment = graphql(`
  fragment JobDetailsFooterInvitationBanner on JobInvitation {
    __typename
    id
    invitationStatus: status
    acceptedAt
    createdAt
    acceptedAt
    message
    sentBy {
      id
      ...UserAvatarWithNameAndTitle
    }
  }
`)

interface Props {
  jobInvitation: FragmentType<typeof JobDetailsFooterInvitationBannerFragment> | undefined | null
  isCreativeJobPosting: boolean
}

export const JobDetailsFooterInvitationBanner: FC<Props> = props => {
  const [showNote, setShowNote] = React.useState(false)
  const { isCreativeJobPosting } = props
  const jobInvitation = useFragment(JobDetailsFooterInvitationBannerFragment, props.jobInvitation)

  const hasRecievedInvitation = jobInvitation?.invitationStatus === JobInvitationStatus.Sent

  const hasNote = jobInvitation?.message

  if (!hasRecievedInvitation) {
    return null
  }

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-row items-center justify-between">
        <JobStatusTag showStatusPrefix={false} status={JobStatusTagStatus.Invited} />
        <div className="flex flex-row items-center justify-between">
          <div className="flex shrink-0 flex-row items-center gap-x-4">
            <Text variant="body2" className="text-fg-2">
              {formatDistanceToNow(jobInvitation?.createdAt ?? adjustDateForTimezone(new Date().toISOString()), {
                addSuffix: true,
              })}
            </Text>
            {hasNote && (
              <div
                className="flex cursor-pointer flex-row items-center gap-x-1 rounded-md px-2 py-1 hover:bg-bg-2"
                onClick={() => setShowNote(!showNote)}>
                {!showNote && <ChatBubbleLeftIcon className="hidden size-[18px] sm:flex" />}
                <Text variant="caption">{showNote ? "Collapse" : "View note"}</Text>
                <ChevronUpIcon
                  className={clsx(
                    "ml-auto size-[22px] cursor-pointer self-end stroke-fg-1 transition-all",
                    !showNote ? "rotate-180" : ""
                  )}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {hasNote && (
        <div className={clsx("", showNote ? "mt-4 h-auto opacity-100" : "pointer-events-none h-0 opacity-0")}>
          <div className="flex flex-col gap-y-3 rounded-lg border border-bg-3 bg-bg-2 p-5">
            <UserAvatarWithNameAndTitle
              user={jobInvitation?.sentBy}
              isCreative={isCreativeJobPosting}
              size="md"
              variant="default"
            />
            <Text>{jobInvitation?.message}</Text>
          </div>
        </div>
      )}
    </div>
  )
}
